<template>
  <div class="admin-page">
    <div class="admin-page-inner admin-page-inner--compact">
      <div class="admin-page-header">
        <div class="admin-page-heading">
          <button
            type="button"
            class="admin-button--secondary admin-button--sm"
            @click="$router.push({ name: 'AdminData' })"
          >
            <i class="fa fa-arrow-left mr-2" aria-hidden="true"></i>Zurück
          </button>
          <div>
            <span class="admin-kicker">Datenverwaltung</span>
            <h1 class="admin-page-title">Feiertage</h1>
          </div>
        </div>
      </div>
      <section class="admin-panel">
        <div class="admin-panel__body">
          <p class="text-gray-600 text-sm mb-4">
            Die hier hinterlegten Feiertage werden bei der Berechnung des Feiertagszuschlags
            (Zuschlagsstunden, DATEV-Export, Rechnungsstellung) berücksichtigt. Feiertage
            können ganztägig gelten oder nur innerhalb eines Zeitfensters (z. B. ab 14:00).
          </p>
          <div v-if="loading" class="text-center py-8">Lade Feiertage...</div>
          <div v-else>
            <div class="border border-gray-300 rounded p-4 mb-4 bg-gray-50">
              <h2 class="font-bold mb-3">{{ form.id ? 'Feiertag bearbeiten' : 'Neuen Feiertag anlegen' }}</h2>
              <div class="flex flex-wrap items-end gap-3">
                <label class="flex flex-col text-sm">
                  <span class="mb-1">Name</span>
                  <input v-model="form.name" class="border px-2 py-1" placeholder="z. B. Heiligabend" />
                </label>
                <label class="flex flex-col text-sm">
                  <span class="mb-1">Art</span>
                  <select v-model="form.type" class="border px-2 py-1">
                    <option value="recurring">Jährlich wiederkehrend</option>
                    <option value="once">Einmalig</option>
                    <option value="easter">Osterbezogen</option>
                  </select>
                </label>
                <label v-if="form.type !== 'easter'" class="flex flex-col text-sm">
                  <span class="mb-1">Datum{{ form.type === 'recurring' ? ' (Jahr egal)' : '' }}</span>
                  <input v-model="form.date" type="date" class="border px-2 py-1" />
                </label>
                <label v-else class="flex flex-col text-sm">
                  <span class="mb-1">Tage relativ zu Ostersonntag</span>
                  <input v-model="form.easterOffset" type="number" class="border px-2 py-1 w-24" placeholder="-2" />
                </label>
                <label class="flex flex-col text-sm">
                  <span class="mb-1">Von (leer = ganztägig)</span>
                  <input v-model="form.from" type="time" class="border px-2 py-1" />
                </label>
                <label class="flex flex-col text-sm">
                  <span class="mb-1">Bis</span>
                  <input v-model="form.to" type="time" class="border px-2 py-1" />
                </label>
                <button
                  type="button"
                  @click="submitForm"
                  :disabled="!canSubmit"
                  class="admin-button--primary admin-button--sm disabled:opacity-50"
                >
                  {{ form.id ? 'Speichern' : 'Hinzufügen' }}
                </button>
                <button
                  v-if="form.id"
                  type="button"
                  @click="resetForm"
                  class="admin-button--secondary admin-button--sm"
                >
                  Abbrechen
                </button>
              </div>
              <p class="text-xs text-gray-500 mt-2">
                Liegt „Bis“ vor „Von“, gilt das Zeitfenster bis in den Folgetag hinein (z. B. 18:00–02:00).
                Für osterbezogene Feiertage z. B.: Karfreitag = -2, Ostermontag = +1, Christi Himmelfahrt = +39.
              </p>
              <p v-if="formError" class="text-sm text-red-600 mt-2">{{ formError }}</p>
            </div>
            <div class="mb-2">
              <button type="button" @click="seedDefaults" class="admin-button--secondary admin-button--sm">
                Standard-Feiertage ergänzen
              </button>
            </div>
            <table class="min-w-full table-auto border border-gray-300">
              <thead>
                <tr>
                  <th class="px-4 py-2 border text-left">Name</th>
                  <th class="px-4 py-2 border text-left">Termin</th>
                  <th class="px-4 py-2 border text-left">Zeitraum</th>
                  <th class="px-4 py-2 border text-left">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="holiday in holidays" :key="holiday.id">
                  <td class="border px-4 py-2">{{ holiday.name }}</td>
                  <td class="border px-4 py-2">{{ describeTermin(holiday) }}</td>
                  <td class="border px-4 py-2">{{ describeZeitraum(holiday) }}</td>
                  <td class="border px-4 py-2">
                    <button type="button" @click="editHoliday(holiday)" class="admin-button--secondary admin-button--sm mr-2">Bearbeiten</button>
                    <button type="button" @click="deleteHoliday(holiday)" class="admin-button--danger admin-button--sm mr-2">Löschen</button>
                  </td>
                </tr>
                <tr v-if="!holidays || holidays.length === 0">
                  <td colspan="4" class="text-gray-400 px-4 py-2">Keine Einträge</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import CASP from '@/CASP.js';

const EMPTY_FORM = {
  id: null,
  name: '',
  type: 'recurring',
  date: '',
  easterOffset: null,
  from: '',
  to: '',
};

export default {
  data() {
    return {
      loading: true,
      holidays: [],
      form: { ...EMPTY_FORM },
      formError: '',
    };
  },
  computed: {
    canSubmit() {
      if (!this.form.name.trim()) return false;
      if (this.form.type === 'easter') {
        return this.form.easterOffset !== null && this.form.easterOffset !== '';
      }
      return !!this.form.date;
    },
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '—';
      const [year, month, day] = dateStr.split('-');
      return `${day}.${month}.${year}`;
    },
    describeTermin(holiday) {
      if (holiday.type === 'easter') {
        const offset = holiday.easterOffset > 0 ? `+${holiday.easterOffset}` : `${holiday.easterOffset}`;
        const next = holiday.nextDate ? ` (→ ${this.formatDate(holiday.nextDate)})` : '';
        return `Ostern ${offset}${next}`;
      }
      if (holiday.type === 'recurring') {
        const short = this.formatDate(holiday.date).slice(0, 6);
        const next = holiday.nextDate ? ` → ${this.formatDate(holiday.nextDate)}` : '';
        return `${short} jährlich${next}`;
      }
      return this.formatDate(holiday.date);
    },
    describeZeitraum(holiday) {
      if (!holiday.from && !holiday.to) return 'ganztägig';
      const from = holiday.from || '00:00';
      const to = holiday.to || '24:00';
      const wrap = holiday.to && holiday.from && holiday.to < holiday.from ? ' (bis Folgetag)' : '';
      return `${from} – ${to} Uhr${wrap}`;
    },
    loadData() {
      CASP.getHolidays((status, result) => {
        if (status == 200) {
          this.holidays = result || [];
          this.loading = false;
        }
      });
    },
    buildPayload() {
      return {
        name: this.form.name,
        date: this.form.type === 'easter' ? null : this.form.date,
        recurring: this.form.type === 'recurring',
        easterOffset: this.form.type === 'easter' ? this.form.easterOffset : null,
        from: this.form.from,
        to: this.form.to,
      };
    },
    submitForm() {
      this.formError = '';
      const payload = this.buildPayload();
      const done = (status, result) => {
        if (status == 200) {
          this.loadData();
          this.resetForm();
        } else {
          this.formError = (result && result.desc) || 'Fehler beim Speichern';
        }
      };
      if (this.form.id) {
        CASP.updateCoreData('holidays', this.form.id, payload, done);
      } else {
        CASP.addCoreData('holidays', payload, done);
      }
    },
    editHoliday(holiday) {
      this.form = {
        id: holiday.id,
        name: holiday.name,
        type: holiday.type,
        date: holiday.date || '',
        easterOffset: holiday.easterOffset,
        from: holiday.from || '',
        to: holiday.to || '',
      };
      this.formError = '';
    },
    resetForm() {
      this.form = { ...EMPTY_FORM };
      this.formError = '';
    },
    deleteHoliday(holiday) {
      if (confirm(`Feiertag „${holiday.name}“ wirklich löschen?`)) {
        CASP.deleteCoreData('holidays', holiday.id, (status) => {
          if (status == 200) {
            this.loadData();
          }
        });
      }
    },
    seedDefaults() {
      CASP.seedHolidays((status, result) => {
        if (status == 200) {
          this.loadData();
          if (result && result.added === 0) {
            alert('Alle Standard-Feiertage sind bereits vorhanden.');
          }
        }
      });
    },
  },
  created() {
    this.loadData();
    document.getElementById('mainspinner').style.display = 'none';
    if (localStorage) localStorage.setItem('last', '/admin/data/holidays');
  },
};
</script>
